import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  type JSX,
  type ReactNode,
} from 'react';
import type {
  TAudioPlayerAction,
  TAudioPlayerContextValue,
  TAudioPlayerState,
  TAudioTrack,
} from '../types/audioPlayer.types';

const STORAGE_KEY_VOLUME = 'audioPlayer:volume';
const STORAGE_KEY_PLAYBACK_RATE = 'audioPlayer:playbackRate';

const getStoredVolume = (): number => {
  const stored = localStorage.getItem(STORAGE_KEY_VOLUME);
  return stored ? parseFloat(stored) : 1;
};

const getStoredPlaybackRate = (): number => {
  const stored = localStorage.getItem(STORAGE_KEY_PLAYBACK_RATE);
  return stored ? parseFloat(stored) : 1;
};

const initialState: TAudioPlayerState = {
  track: null,
  isPlaying: false,
  currentTime: 0,
  duration: 0,
  volume: getStoredVolume(),
  isMuted: false,
  playbackRate: getStoredPlaybackRate(),
  isLoading: false,
  isMiniPlayerVisible: false,
};

function audioPlayerReducer(
  state: TAudioPlayerState,
  action: TAudioPlayerAction
): TAudioPlayerState {
  switch (action.type) {
    case 'SET_TRACK':
      return {
        ...state,
        track: action.payload,
        currentTime: 0,
        duration: 0,
        isLoading: true,
      };
    case 'PLAY':
      return { ...state, isPlaying: true };
    case 'PAUSE':
      return { ...state, isPlaying: false };
    case 'TOGGLE_PLAY':
      return { ...state, isPlaying: !state.isPlaying };
    case 'SET_CURRENT_TIME':
      return { ...state, currentTime: action.payload };
    case 'SET_DURATION':
      return { ...state, duration: action.payload, isLoading: false };
    case 'SET_VOLUME':
      localStorage.setItem(STORAGE_KEY_VOLUME, String(action.payload));
      return { ...state, volume: action.payload, isMuted: action.payload === 0 };
    case 'TOGGLE_MUTE':
      return { ...state, isMuted: !state.isMuted };
    case 'SET_PLAYBACK_RATE':
      localStorage.setItem(STORAGE_KEY_PLAYBACK_RATE, String(action.payload));
      return { ...state, playbackRate: action.payload };
    case 'SET_LOADING':
      return { ...state, isLoading: action.payload };
    case 'SHOW_MINI_PLAYER':
      return { ...state, isMiniPlayerVisible: true };
    case 'HIDE_MINI_PLAYER':
      return { ...state, isMiniPlayerVisible: false };
    case 'CLOSE_PLAYER':
      return { ...initialState, volume: state.volume, playbackRate: state.playbackRate };
    default:
      return state;
  }
}

export const AudioPlayerContext = createContext<TAudioPlayerContextValue | null>(null);

type TAudioPlayerProviderProps = {
  children: ReactNode;
};

export function AudioPlayerProvider({ children }: TAudioPlayerProviderProps): JSX.Element {
  const [state, dispatch] = useReducer(audioPlayerReducer, initialState);
  const audioRef = useRef<HTMLAudioElement>(null);

  // Sync play/pause state
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    if (state.isPlaying) {
      audio.play().catch(() => {
        dispatch({ type: 'PAUSE' });
      });
    } else {
      audio.pause();
    }
  }, [state.isPlaying, state.track?.url]);

  // Sync volume and mute state
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    // Use muted property for iOS Safari compatibility
    audio.muted = state.isMuted;

    // Set volume for desktop browsers (iOS ignores this but it doesn't hurt)
    audio.volume = state.volume;
  }, [state.volume, state.isMuted]);

  // Sync playback rate
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    audio.playbackRate = state.playbackRate;
  }, [state.playbackRate]);

  // Media Session API for lock screen controls
  useEffect(() => {
    if (!state.track || !('mediaSession' in navigator)) return;

    navigator.mediaSession.metadata = new MediaMetadata({
      title: state.track.title,
      artist: 'Grace Gainesville',
      album: state.track.date,
    });

    navigator.mediaSession.setActionHandler('play', () => dispatch({ type: 'PLAY' }));
    navigator.mediaSession.setActionHandler('pause', () => dispatch({ type: 'PAUSE' }));
    navigator.mediaSession.setActionHandler('seekbackward', () => {
      const audio = audioRef.current;
      if (audio) {
        audio.currentTime = Math.max(0, audio.currentTime - 15);
      }
    });
    navigator.mediaSession.setActionHandler('seekforward', () => {
      const audio = audioRef.current;
      if (audio) {
        audio.currentTime = Math.min(audio.duration || 0, audio.currentTime + 15);
      }
    });

    return (): void => {
      navigator.mediaSession.setActionHandler('play', null);
      navigator.mediaSession.setActionHandler('pause', null);
      navigator.mediaSession.setActionHandler('seekbackward', null);
      navigator.mediaSession.setActionHandler('seekforward', null);
    };
  }, [state.track]);

  const play = useCallback(() => {
    dispatch({ type: 'PLAY' });
  }, []);

  const pause = useCallback(() => {
    dispatch({ type: 'PAUSE' });
  }, []);

  const togglePlay = useCallback(() => {
    dispatch({ type: 'TOGGLE_PLAY' });
  }, []);

  const seek = useCallback((time: number) => {
    const audio = audioRef.current;
    if (audio) {
      audio.currentTime = time;
      dispatch({ type: 'SET_CURRENT_TIME', payload: time });
    }
  }, []);

  const skipForward = useCallback((seconds = 15) => {
    const audio = audioRef.current;
    if (audio) {
      audio.currentTime = Math.min(audio.duration || 0, audio.currentTime + seconds);
    }
  }, []);

  const skipBackward = useCallback((seconds = 15) => {
    const audio = audioRef.current;
    if (audio) {
      audio.currentTime = Math.max(0, audio.currentTime - seconds);
    }
  }, []);

  const setVolume = useCallback((volume: number) => {
    dispatch({ type: 'SET_VOLUME', payload: volume });
  }, []);

  const toggleMute = useCallback(() => {
    dispatch({ type: 'TOGGLE_MUTE' });
  }, []);

  const setPlaybackRate = useCallback((rate: number) => {
    dispatch({ type: 'SET_PLAYBACK_RATE', payload: rate });
  }, []);

  const loadTrack = useCallback((track: TAudioTrack) => {
    dispatch({ type: 'SET_TRACK', payload: track });
    dispatch({ type: 'HIDE_MINI_PLAYER' });
  }, []);

  const closePlayer = useCallback(() => {
    dispatch({ type: 'CLOSE_PLAYER' });
  }, []);

  const showMiniPlayer = useCallback(() => {
    dispatch({ type: 'SHOW_MINI_PLAYER' });
  }, []);

  const hideMiniPlayer = useCallback(() => {
    dispatch({ type: 'HIDE_MINI_PLAYER' });
  }, []);

  const handleTimeUpdate = useCallback(() => {
    const audio = audioRef.current;
    if (audio) {
      dispatch({ type: 'SET_CURRENT_TIME', payload: audio.currentTime });
    }
  }, []);

  const handleLoadedMetadata = useCallback(() => {
    const audio = audioRef.current;
    if (audio) {
      dispatch({ type: 'SET_DURATION', payload: audio.duration });
    }
  }, []);

  const handleEnded = useCallback(() => {
    dispatch({ type: 'PAUSE' });
  }, []);

  const handleCanPlay = useCallback(() => {
    dispatch({ type: 'SET_LOADING', payload: false });
  }, []);

  const value = useMemo<TAudioPlayerContextValue>(
    () => ({
      state,
      dispatch,
      audioRef,
      play,
      pause,
      togglePlay,
      seek,
      skipForward,
      skipBackward,
      setVolume,
      toggleMute,
      setPlaybackRate,
      loadTrack,
      closePlayer,
      showMiniPlayer,
      hideMiniPlayer,
    }),
    [
      state,
      dispatch,
      audioRef,
      play,
      pause,
      togglePlay,
      seek,
      skipForward,
      skipBackward,
      setVolume,
      toggleMute,
      setPlaybackRate,
      loadTrack,
      closePlayer,
      showMiniPlayer,
      hideMiniPlayer,
    ]
  );

  return (
    <AudioPlayerContext.Provider value={value}>
      {children}
      {state.track && (
        <audio
          ref={audioRef}
          src={state.track.url}
          onTimeUpdate={handleTimeUpdate}
          onLoadedMetadata={handleLoadedMetadata}
          onEnded={handleEnded}
          onCanPlay={handleCanPlay}
          preload="metadata"
        />
      )}
    </AudioPlayerContext.Provider>
  );
}

export default AudioPlayerProvider;
